import { routingKeyToCounty } from './routingKeyToCounty';

export const rentIncreaseCounties = Array.from(
  new Set(Object.values(routingKeyToCounty).map((county) => county.toLowerCase()))
).sort();

export const toTitleCase = (value: string) =>
  value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

interface CountyEnrichment {
  intro: string;
  pressurePoint: string;
  strategyBullets: string[];
  watchouts: string[];
}

const DUBLIN_BELT = new Set(['dublin', 'kildare', 'meath', 'wicklow']);
const LARGE_CITIES = new Set(['cork', 'galway', 'limerick', 'waterford']);

export const getCountyEnrichment = (county: string): CountyEnrichment => {
  const countyName = toTitleCase(county);

  if (DUBLIN_BELT.has(county)) {
    return {
      intro: `${countyName} is usually one of the highest-demand rental markets in Ireland, so rent notices are often scrutinized by tenants and advisors before acceptance.`,
      pressurePoint:
        'A small euro difference can still trigger disputes when tenants compare notices with market alternatives in nearby commuter areas.',
      strategyBullets: [
        'Use a clean calculation table in your notice showing current rent, max lawful rent, and proposed rent.',
        'Reference the exact date rent was last set and keep proof of that date with your tenancy records.',
        'Issue notices early with clear effective dates to avoid timeline disputes.',
      ],
      watchouts: [
        'Avoid rounding up above the lawful cap, even by small amounts.',
        'Do not rely on verbal notice only; keep a written record with timestamp.',
        'Make sure the effective date gives enough notice period for the tenancy type.',
      ],
    };
  }

  if (LARGE_CITIES.has(county)) {
    return {
      intro: `${countyName} has active rental turnover and competitive pricing, which means tenants regularly benchmark notices against nearby listings.`,
      pressurePoint:
        'In growth markets, landlords often over-index on asking rents and accidentally exceed lawful review limits.',
      strategyBullets: [
        'Keep your proposed rent linked to the calculated cap, not only to current market asking levels.',
        'Store the HICP/CPI basis used for your calculation to support your notice if challenged.',
        'Send a transparent notice with clear line items so tenants can validate it quickly.',
      ],
      watchouts: [
        'Skipping calculation evidence can reduce trust and increase dispute risk.',
        'Backdating effective dates creates compliance exposure.',
        'Not reflecting tenancy-specific restrictions can invalidate a notice.',
      ],
    };
  }

  return {
    intro: `${countyName} rental reviews are still compliance-sensitive, especially where affordability pressure is rising and tenant awareness is increasing.`,
    pressurePoint:
      'Many invalid notices happen from date handling mistakes rather than the rent math itself.',
    strategyBullets: [
      'Use this calculator before drafting your notice to avoid avoidable errors.',
      'Keep every supporting document in one file for faster dispute response.',
      'Use plain-language notice wording so both sides understand the rent path.',
    ],
    watchouts: [
      'Incorrect last rent date leads to wrong cap calculation.',
      'Missing rent breakdown details can trigger tenant pushback.',
      'Ignoring delivery proof can create enforcement problems later.',
    ],
  };
};

export const getCountyFaq = (county: string) => {
  const countyName = toTitleCase(county);

  return [
    {
      q: `How do I calculate a lawful rent increase in ${countyName}?`,
      a: `Use your current rent, the date rent was last set, and the current index data. This page calculates a cap-based maximum and compares your proposed rent against it.`,
    },
    {
      q: `Can a landlord in ${countyName} send a rent review by email only?`,
      a: 'A written notice with clear dates, rent figures, and evidence is essential. Keep proof of delivery and supporting calculation records.',
    },
    {
      q: `What happens if the proposed rent is above the lawful cap in ${countyName}?`,
      a: 'It may be challenged by the tenant, and the notice can fail compliance checks. This tool highlights when the proposed rent is above the calculated limit.',
    },
    {
      q: `Is this a replacement for official RTB guidance in ${countyName}?`,
      a: 'No. This is an informational tool to help prepare compliant notices. Always confirm with official RTB guidance and qualified legal advice when needed.',
    },
  ];
};
